<script>
import constants from "@/libs/Constants";
import Toast from "@/components/Toast.vue";
import Slider from "@/components/Slider.vue";
import ModalWindow from "@/components/ModalWindow.vue";
import {SimulationAnthropicWorksAbstract} from '@/libs/SimulationData';
import {
  anthropicWorkBadgeColorClass,
  disposeTooltipsByClass,
  getWaterInfrastructureTypeI18NLabel,
  setupTooltipsByClass
} from "@/libs/Helpers";
import RadioOnOff from "@/components/RadioOnOff.vue";

export default {
  name: "AnthropicWorksModal",
  emits: ['onModalClickClose', "onClickSaveChanges", "onClickResetValues"],
  components: {
    RadioOnOff,
    Toast,
    Slider,
    ModalWindow
  },
  props: {
    propModalId: String,
    propStatus: Boolean,
    propType: String,
    propModel: {type: SimulationAnthropicWorksAbstract, default: null},
  },
  watch: {
    propModel(newValue, oldValue) {
      //New object is the cloned version of the model sent
      this.modelObject = newValue.clone();
    }
  },
  data() {
    return {
      constants,

      modelObject: null,

      toasts: {
        name: '',
        type: '',
        class: '',
        status: {
          success: false,
          reset: false,
        }
      }
    }
  },
  methods: {
    onClickCloseModal() {
      this.$emit('onModalClickClose');
    },
    onClickSaveChanges() {
      this.toasts.name = this.modelName;
      this.toasts.type = this.modelTypeLabel;
      this.toasts.class = this.badgeColorClass;

      this.toasts.status.success = true;
      this.$emit('onClickSaveChanges', this.modelObject);
    },
    resetValuesToDefault() {
      if (this.modelObject !== null)
        this.modelObject.setDefaultValues();
    },
    onToastHidden() {
      this.toasts.status.success = false;

      this.toasts.name = '';
      this.toasts.type = '';
      this.toasts.class = '';
    },
    tooltipCreate() {
      let generalTooltipClass = '.tooltip-slider-init'
      disposeTooltipsByClass(generalTooltipClass);
      setupTooltipsByClass(generalTooltipClass);
    },
  },
  computed: {
    modelType() {
      return this.modelObject !== null ? this.modelObject.type : null;
    },
    modelTypeLabel() {
      let modelType = getWaterInfrastructureTypeI18NLabel(this.modelType);
      return modelType !== null ? this.$t(modelType) : '';
    },
    modelName() {
      return this.propModel !== null ? this.propModel.name : '';
    },
    badgeColorClass() {
      return anthropicWorkBadgeColorClass(this.modelType);
    },
    modelIsModified() {
      return this.propModel !== null && this.modelObject !== null ?
          this.propModel.compareEqual(this.modelObject) === false :
          true
    },
    modelIsDefault() {
      return this.modelObject !== null ?
          this.modelObject.hasDefaultValues :
          false
    }
  },
  updated() {
    this.tooltipCreate();
  }
}
</script>

<template>
  <!-- Modal for anthropic work editing -->
  <ModalWindow
      :prop-id="propModalId"
      :prop-status="propStatus"
      :prop-static="true"
      :prop-disable-close-button-footer="true"
      @on-close-modal-click="onClickCloseModal">
    <template v-slot:title>
      <span class="me-1">{{ modelName }}</span>
      <span class="badge rounded-pill" :class="badgeColorClass">
          {{ modelTypeLabel }}
      </span>
    </template>
    <div class="modal-body">
      <div class="row">
        <!-- Dam / lake -->
        <template v-if="modelType === constants.LAYERS.DAM_LAKE">
          <div class="col-12">
            <Slider :prop-title="$t('WorksInputs.RegulationVolume')"
                    v-model="modelObject.dischargeTurbineFlow"
                    :prop-minimum="0.5"
                    :prop-maximum="1.5"
                    :prop-decimals-minimum="1"
                    :prop-decimals-maximum="1"
                    :prop-tooltip-text="$t('Glossary.regulation_level.description')"
            />
          </div>
        </template>

        <!-- Derivation -->
        <template v-if="modelType === constants.LAYERS.DERIVATION">
          <div class="col-12">
            <RadioOnOff
                prop-id="anthropicWorksDerivation"
                :prop-title="$t('WorksInputs.DischargeDerivation')"
                :prop-on-title="$t('WorksInputs.DischargeDerivationStatus.Open')"
                :prop-off-title="$t('WorksInputs.DischargeDerivationStatus.Closed')"
                v-model="modelObject.dischargeDerivation"
            />
          </div>
        </template>

        <!-- Hydroelectric plants -->
        <template v-if="modelType === constants.LAYERS.PLANT">
          <div class="col-12">
            <Slider :prop-title="$t('WorksInputs.DischargeMaximumTurbine')"
                    v-model="modelObject.dischargeTurbineMaximum"
                    :prop-minimum="0.1"
                    :prop-maximum="5"
                    :prop-decimals-minimum="1"
                    :prop-decimals-maximum="0"
            />
          </div>
        </template>
      </div>
    </div>
    <template v-slot:footer>
      <button type="button" class="btn btn-warning"
              :disabled="modelIsDefault === true"
              @click="resetValuesToDefault">
        <i class="bi bi-arrow-clockwise me-2"></i>
        <span>{{ $t('Actions.ResetDefault') }}</span>
      </button>
      <button type="button" class="btn btn-success text-white"
              :disabled="modelIsModified === false"
              @click="onClickSaveChanges">
        <i class="bi bi-pencil-square me-2"></i>
        <span>{{ $t('Actions.Update') }}</span>
      </button>
    </template>
  </ModalWindow>

  <!-- Toasts -->
  <Toast prop-id="toastAnthropicWorkSuccess"
         prop-type="success"
         :prop-trigger="toasts.status.success"
         @on-toast-hidden="onToastHidden">
    <template v-slot:body>
      <div>
        <span class="me-1 fw-semibold">{{ toasts.name }}</span>
        <span class="badge rounded-pill" :class="toasts.class">
          {{ toasts.type }}
      </span>
      </div>
      <span>{{ $t('alerts.anthropicWorkUpdatedSuccess') }}</span>
    </template>
  </Toast>
</template>